<template>
   <div class="start-page-bg">
      <div class="container">
         <div class="row align-items-center justify-content-center vh-100">
            <div class="card col-xl-5 col-md-8 col-lg-6 col-sm-12" style="top: 5%;">

               <div class="card-body">

                  <div class="text-center justify-content-center mb-4">
                     <img src="@/assets/img/vertex_logo.png" alt="spark_logo" class="img-fluid w-50" />
                  </div>

                  <div class="text-center justify-content-center">
                     <h3 class="card-title"><strong>Request for Penetration Testing Quote</strong></h3>
                  </div>

                  <div class="m-3">
                     <h6 class="card-subtitle">Please use this form to request a proposal for Penetration Testing
                        services.</h6>
                  </div>

                  <div class="m-3">
                     <h6 class="card-subtitle">Once all details have been received, your proposal will be sent the next
                        business day.</h6>
                  </div>

                  <hr />

                  <div class="row m-3 text-center">
                     <h6 class="card-subtitle text-muted">Date: <strong>{{ this.currentDate }}</strong></h6>
                  </div>

                  <form ref="pentestFormRef" class="row mx-4 g-0" style="gap: 0.5rem">

                     <div class="form-floating form-div">
                        <select v-if="abnRecordFound && organisationNameOptions.length" id="organisation" class="form-select form-control pt-3" v-model="lead.organisation" @change="v$.lead.organisation.$touch">
                           <option v-for="name in organisationNameOptions" :key="name" :value="name">{{ name }}</option>
                        </select>
                        <input v-else type="text" id="organisation" v-model.trim="lead.organisation"
                           @input="handleOrgInput" @blur="v$.lead.organisation.$touch" class="form-control" placeholder="Organisation Name" />
                        <label for="organisation" class="text-muted">Organisation Name <span
                              class="requiredField">*</span></label>
                        <span class="form-text text-danger error-msg" v-if="v$.lead.organisation.$error">
                           <span v-if="v$.lead.organisation.required && v$.lead.organisation.required.$invalid">Organisation Name is required.</span>
                           <span v-else-if="v$.lead.organisation.validOrg && v$.lead.organisation.validOrg.$invalid">Special characters (&lt;, &gt;, &quot;, `, \) are not allowed.</span>
                        </span>
                     </div>

                     <div>
                        <VueMultiselect v-model="lead.selectedCountry" :options="countryOptions" :multiple="false"
                           :close-on-select="true" :clear-on-select="false" :preserve-search="true"
                           placeholder="Select a country" label="name" track-by="code" :preselect-first="true"
                           :disabled="lead.isCharity">
                           <template #option="{ option }">
                              <div class="option-with-flag">
                                 <span class="flag">{{ option.flag }}</span>
                                 &nbsp;
                                 <span>{{ option.name }}</span>
                              </div>
                           </template>

                           <template #singleLabel="{ option }">
                              <div class="option-with-flag">
                                 <span class="flag">{{ option.flag }}</span>
                                 &nbsp;
                                 <span>{{ option.name }}</span>
                              </div>
                           </template>
                        </VueMultiselect>
                     </div>

                     <div class="form-floating">
                        <input type="text" id="firstName" v-model.trim="lead.firstName" @blur="v$.lead.firstName.$touch"
                           class="form-control" placeholder="First Name" />
                        <label for="firstName" class="text-muted">First Name <span
                              class="requiredField">*</span></label>
                        <span class="form-text text-danger error-msg" v-if="v$.lead.firstName.$error">
                           First Name is required.
                        </span>
                     </div>

                     <div class="form-floating">
                        <input type="text" id="lastName" v-model.trim="lead.lastName" @blur="v$.lead.lastName.$touch"
                           class="form-control" placeholder="Last Name" />
                        <label for="lastName" class="text-muted">Last Name <span class="requiredField">*</span></label>
                        <span class="form-text text-danger error-msg" v-if="v$.lead.lastName.$error">
                           Last Name is required.
                        </span>
                     </div>

                     <div class="form-floating">
                        <input type="email" id="email" v-model.trim="lead.email" @blur="v$.lead.email.$touch"
                           class="form-control" placeholder="Email" />
                        <label for="email" class="text-muted">Email <span class="requiredField">*</span></label>
                        <span class="form-text text-danger error-msg"
                           v-if="v$.lead.email.$error && v$.lead.email.required.$invalid">
                           Email is required.
                        </span>
                        <span class="form-text text-danger error-msg"
                           v-if="v$.lead.email.$error && v$.lead.email.email.$invalid">
                           Must be a valid email.
                        </span>
                     </div>

                     <vue-tel-input ref="vueTelInputRef" v-model.trim="lead.contactNumber" v-bind="vueTel.props"
                        mode="international"></vue-tel-input>

                     <div class="form-floating">
                        <input type="text" id="abn" v-model.trim="lead.abn" @keyup="handleAbnInput" @blur="handleAbnBlur"
                           class="form-control" :placeholder="businessNumberPlaceholder" :disabled="abnLookup.isLoading" />
                        <label for="abn" class="text-muted">
                           {{ businessNumberLabel }}
                           <span v-if="lead.isCharity" class="requiredField">*</span>
                        </label>
                        <p v-if="abnLookup.isLoading" class="form-text text-info mb-0 small text-start">Validating {{ businessNumberShortLabel }}...</p>
                        <p v-if="!abnLookup.isLoading && abnLookup.message" class="form-text mb-0 small text-start"
                           :class="abnLookup.status === 'success' ? 'text-success' : 'text-danger'">
                           {{ abnLookup.message }}
                        </p>
                        <span class="form-text text-danger error-msg" v-if="v$.lead.abn.$error && !abnLookup.message">
                           <span v-if="v$.lead.abn.required && v$.lead.abn.required.$invalid">{{ businessNumberShortLabel }} is required.</span>
                        </span>
                     </div>

                     <div class="form-check align-items-center">
                        <input class="form-check-input" type="checkbox" :value="true" id="isCharity"
                           v-model.trim="lead.isCharity">
                        <label class="form-check-label pt-1" for="isCharity">
                           <strong>Is a registered charity?</strong>
                        </label>
                     </div>

                     <div class="form-floating">
                        <VueMultiselect id="product" v-model="lead.product" :options="productList" :multiple="true"
                           :close-on-select="true" label="display_name" track-by="id" :searchable="false"
                           placeholder="Please choose at least one..." @blur="v$.lead.product.$touch">
                        </VueMultiselect>
                        <label for="product" class="text-muted">Test Type <span class="requiredField">*</span></label>
                        <span class="form-text text-danger error-msg" v-if="v$.lead.product.$error">
                           Please choose at least one.
                        </span>
                     </div>

                     <span v-if="lead.product.length > 0" class="form-text test-type-error-msg">
                        <i>Please fill up all forms of selected test types</i>
                     </span>
                     <div v-if="lead.product.length > 0" class="accordion" id="pentestTypeForms">

                        <div v-for="(product, index) in lead.product" :key="product.id">
                           <div class="accordion-item test-type-form">

                              <h2 class="accordion-header" :id="'formHeader' + index">
                                 <button class="accordion-button" :class="{ 'collapsed': index > 0 }" type="button"
                                    data-bs-toggle="collapse" :data-bs-target="'#form' + index" aria-expanded="true"
                                    :aria-controls="'form' + index">
                                    <strong>{{ productName(product.name) }} Form</strong>
                                 </button>
                              </h2>

                              <div :id="'form' + index" class="accordion-collapse collapse"
                                 :class="{ 'show': index == 0 }" :aria-labelledby="'formHeader' + index"
                                 data-bs-parent="#pentestTypeForms">
                                 <div v-if="testType(product.name, WEB)" class="accordion-body ">
                                    <WebPenTestForm ref="webPenTestFormRef" action="create" />
                                 </div>
                                 <div v-if="testType(product.name, APP)" class="accordion-body">
                                    <AppPenTestForm ref="appPenTestFormRef" action="create" />
                                 </div>
                                 <div v-if="testType(product.name, INFRA)" class="accordion-body">
                                    <NetworkInfraPenTestForm ref="networkInfraPenTestFormRef" action="create" />
                                 </div>
                              </div>
                           </div>
                        </div>

                     </div>

                     <!-- TIMEFRAME -->
                     <div class="form-control">
                        <label class="form-label">Is there a specific timeframe in which penetration testing needs to be
                           completed?
                           <span class="requiredField">*</span>
                        </label>
                        <div>

                           <div class="form-check form-check-inline">
                              <input class="form-check-input" type="radio" :value=true
                                 v-model.trim="penetrationTest.hasTimeframe" id="hasTimeframe" name="timeframe">
                              <label class="form-check-label" for="hasTimeframe">Yes</label>
                           </div>

                           <div class="form-check form-check-inline">
                              <input class="form-check-input" type="radio" :value=false
                                 v-model.trim="penetrationTest.hasTimeframe" id="noTimeframe" name="timeframe"
                                 @change="onChange()">
                              <label class="form-check-label" for="noTimeframe">No</label>
                           </div>

                        </div>

                        <div v-if="penetrationTest.hasTimeframe" class="form-floating">
                           <textarea id="timeframeDetails" v-model.trim="penetrationTest.timeframeDetails"
                              @blur="v$.penetrationTest.timeframeDetails.$touch" class="form-control"
                              placeholder="Please provide details of the timeframe."></textarea>
                           <label for="timeframeDetails" class="text-muted">Please provide details of the
                              timeframe.</label>
                           <span class="form-text text-danger error-msg"
                              v-if="v$.penetrationTest.timeframeDetails.$error">
                              Timeframe details is required.
                           </span>
                        </div>

                     </div>


                     <!-- END -- TIMEFRAME -->

                     <div class="form-floating">
                        <textarea class="form-control" id="relevantInformation"
                           v-model.trim="penetrationTest.relevantInformation"
                           placeholder="Please provide any additional relevant information."
                           style="height: 150px"></textarea>
                        <label for="relevantInformation" class="text-muted">Please provide any additional relevant
                           information.</label>
                     </div>

                     <div class="form-floating w-50 vw-100">
                        <select class="form-select" id="marketingChannel" v-model.trim="chosen_marketingChannel">
                           <option selected disabled value>Please choose...</option>
                           <option v-for="marketingChannel in marketingChannels" :value="marketingChannel.id"
                              :key="marketingChannel.id">
                              {{ marketingChannel.display_name }}</option>
                        </select>
                        <label for="marketingChannel" class="form-label">(Optional) How did you find Vertex?</label>
                     </div>

                     <div class="form-check align-items-center">
                        <input class="form-check-input" type="checkbox" :value="true" id="isScheduleMeeting"
                           v-model.trim="isScheduleMeeting">
                        <label class="form-check-label pt-1" for="isScheduleMeeting">
                           <strong>Book a meeting with our team to discuss your request.</strong>
                        </label>
                     </div>

                     <div class="mb-3 text-center">
                        <!--   -->
                        <p v-if="!isFormValid && v$.$error" class="form-text text-danger"
                           :class="{ 'd-none': isFormValid }">
                           Please review and fill up all required fields.
                        </p>
                        <button type="button" class="btn btn-info text-light" @click="validateForm()"
                           :disabled="isProcessing"> {{
                              isProcessing ? 'Submitting...' : 'Submit' }} </button>
                     </div>

                  </form>
               </div>

            </div>
         </div>
      </div>
   </div>

   <div v-if="isProcessing" class="loading" :class="{ 'd-none': !isProcessing }">
      <div class="row p-2 justify-content-center" style="background-color: #fff;">

         <div class="text-center mb-2">
            <div class="mb-2"><strong>Submitting request</strong></div>
            <div>Please wait...</div>
         </div>

         <div class="spinner-border" role="status">
            <span class="visually-hidden">Loading...</span>
         </div>
      </div>
   </div>


</template>

<script>
import moment from "moment";
import { useVuelidate } from '@vuelidate/core'
import { required, requiredIf, email } from '@vuelidate/validators'
import { VueTelInput } from 'vue-tel-input'
import 'vue-tel-input/dist/vue-tel-input.css'
import VueMultiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.css'
import "@/assets/scss/vue-multiselect.scss";
import AppPenTestForm from '@/components/Leads/Pentest/AppPenTestForm.vue';
import NetworkInfraPenTestForm from '@/components/Leads/Pentest/NetworkInfraPenTestForm.vue';
import WebPenTestForm from '@/components/Leads/Pentest/WebPenTestForm.vue';
import { countries } from 'country-data';
import {
   isAustralianCountry,
   getBusinessNumberLabel,
   getBusinessNumberShortLabel,
   getBusinessNumberPlaceholder,
   validateBusinessNumber,
} from '@/plugins/businessValidator.js';

const orgNameValidator = (value) => !value || !/[<>"\\`]/.test(value)

const WEB = "Web"
const INFRA = "Infrastructure"
const APP = "App"

function initialState_lead(preselectedCountry) {
   return {
      organisation: '',
      firstName: '',
      lastName: '',
      email: '',
      contactNumber: '',
      abn: '',
      isCharity: false,
      product: [],
      chosen_marketingChannel: "",
      selectedCountry: preselectedCountry,
      isScheduleMeeting: false,
   }
}

function initialState_pentest() {
   return {
      hasTimeframe: false,
      timeframeDetails: '',
      relevantInformation: '',
   }
}

export default {

   components: {
      VueTelInput,
      VueMultiselect,
      // FileUploader,
      AppPenTestForm,
      NetworkInfraPenTestForm,
      WebPenTestForm,
   },

   setup() {
      return { v$: useVuelidate() }
   },

   data() {

      const countryOptions = countries.all.map(country => ({
         code: country.alpha2,
         name: country.name,
         flag: country.emoji,
      }));

      // Find Australia in the countryOptions array
      const preselectedCountry = countryOptions.find(country => country.code === 'AU');


      return {
         currentDate: null,
         lead: {},
         preselectedCountry: preselectedCountry,
         penetrationTest: {
            mobileApp: {},
            networkInfra: {},
            website: {},
         },
         externalInternalList: [
            { name: "External", value: "external" },
            { name: "Internal", value: "internal" }
         ],
         operatingSystemList: [
            { name: "Android", value: "android" },
            { name: "iOS", value: "ios" }
         ],
         testTypes: [],
         leadTypes: [],
         chosen_marketingChannel: "",
         marketingChannels: [],
         leadPriorityTypes: [],
         productList: [],
         vueTel: {
            props: {
               preferredCountries: ['AU', 'US'],
               defaultCountry: "AU",
               validCharactersOnly: true,
               dropdownOptions: {
                  // showDialCodeInSelection: true,
                  showDialCodeInList: true,
                  showFlags: true,
                  showSearchBox: true,
               },
               inputOptions: {
                  autocomplete: "on",
                  showDialCode: true,
                  placeholder: "Contact Number",
                  styleClasses: "form-control",
               },
            }
         },
         isProcessing: false,
         isFormValid: true,
         countryOptions: countryOptions,
         abnRecordFound: false,
         abnDetails: null,
         abnLookup: { isLoading: false, status: 'idle', message: '' },
         abnDebounceTimer: null,
      }
   },

   validations() {
      return {
         lead: {
            organisation: { required, validOrg: orgNameValidator },
            firstName: { required },
            lastName: { required },
            email: { required, email },
            abn: {
               required: requiredIf(() => {
                  return !!this.lead.isCharity;
               }),
               validBusinessNumber: (value) => {
                  const countryCode = this.lead.selectedCountry?.code || 'AU';
                  return validateBusinessNumber(value, countryCode);
               }
            },
            product: { required },
         },
         penetrationTest: {
            hasTimeframe: { required },
            timeframeDetails: { requiredIf: requiredIf(this.penetrationTest.hasTimeframe) },
         }

      }
   },

   mounted() {
      // Track the PageView event for this specific form
      if (window.fbq) {
         window.fbq('track', 'PageView');
      }
   },

   created() {
      this.init()

      //declare constants
      this.APP = APP
      this.INFRA = INFRA
      this.WEB = WEB
   },

   computed: {
      leadCountryCode() {
         return this.lead.selectedCountry?.code || 'AU';
      },
      isAustralianLead() {
         return isAustralianCountry(this.leadCountryCode);
      },
      businessNumberLabel() {
         return getBusinessNumberLabel(this.leadCountryCode);
      },
      businessNumberShortLabel() {
         return getBusinessNumberShortLabel(this.leadCountryCode);
      },
      businessNumberPlaceholder() {
         return getBusinessNumberPlaceholder(this.leadCountryCode);
      },
      selectedProduct() {
         return this.lead.product
      },
      organisationNameOptions() {
         if (!this.abnRecordFound || !this.abnDetails) return [];
         const names = [this.abnDetails.entity_name];
         if (this.abnDetails.business_names && this.abnDetails.business_names.length > 0) {
            const businessNames = this.abnDetails.business_names.map(bn => bn.name).filter(Boolean);
            names.push(...businessNames);
         }
         return [...new Set(names)];
      },
   },

   watch: {
      selectedProduct() {
         this.checkSelectedProduct()
      },
      'lead.isCharity'(newVal) {
         if (newVal) {
            const au = this.countryOptions.find(c => c.code === 'AU');
            if (au) {
               this.lead.selectedCountry = au;
            }
            if (this.lead.abn) {
               this.lookupABN();
            }
         } else {
            if (this.lead.abn) {
               this.lookupABN();
            } else {
               this.abnLookup = { isLoading: false, status: 'idle', message: '' };
            }
         }
      }
   },

   methods: {
      init() {
         this.currentDate = moment(new Date()).format("DD-MMM-YYYY");

         //load initial state of object data
         Object.assign(this.lead, initialState_lead(this.preselectedCountry));
         Object.assign(this.penetrationTest, initialState_pentest());

         this.getProductList()
         this.getMarketingChannelList()
      },

      resetForm() {
         this.v$.$reset()
         Object.assign(this.lead, initialState_lead(this.preselectedCountry));
         Object.assign(this.penetrationTest, initialState_pentest());
         this.setDefaultCountryCode()
         this.chosen_marketingChannel = ""
         this.abnRecordFound = false
         this.abnDetails = null
         this.abnLookup = { isLoading: false, status: 'idle', message: '' }
      },

      setDefaultCountryCode() {
         // For Contact Number field - Change back to AU flag
         this.$refs.vueTelInputRef.activeCountryCode = "AU"
         this.lead.contactNumber = "+61"
      },

      disableFormButton() {

         if (this.v$.$invalid || this.v$.$error || this.isProcessing) {
            return true
         }

         return false
      },

      handleOrgInput() {
         if (this.lead.organisation) {
            this.lead.organisation = this.lead.organisation.replace(/[<>"\\`]/g, '');
         }
      },

      handleAbnInput() {
         clearTimeout(this.abnDebounceTimer);
         if (!this.lead.abn || this.lead.abn.trim() === '') {
            this.abnRecordFound = false;
            this.abnDetails = null;
            this.abnLookup = { isLoading: false, status: 'idle', message: '' };
            return;
         }
         this.abnDebounceTimer = setTimeout(() => { this.lookupABN(); }, 1000);
      },

      handleAbnBlur() {
         this.v$.lead.abn.$touch();
         if (this.abnDebounceTimer) {
            clearTimeout(this.abnDebounceTimer);
            this.abnDebounceTimer = null;
            this.lookupABN();
         }
      },

      async lookupABN() {
         if (!this.lead.abn || this.lead.abn.trim() === '') {
            this.abnLookup = { isLoading: false, status: 'idle', message: '' };
            this.abnRecordFound = false;
            this.abnDetails = null;
            return;
         }

         if (!this.isAustralianLead) {
            const isValid = validateBusinessNumber(this.lead.abn, this.leadCountryCode);
            if (isValid) {
               this.abnLookup = { isLoading: false, status: 'success', message: `${this.businessNumberShortLabel} format valid.` };
            } else {
               this.abnLookup = { isLoading: false, status: 'error', message: `Invalid ${this.businessNumberShortLabel} format.` };
            }
            this.abnRecordFound = false;
            this.abnDetails = null;
            return;
         }

         const abn = this.lead.abn.replace(/\s/g, '');
         if (!abn || (abn.length !== 11 && abn.length !== 9)) {
            this.abnLookup = { isLoading: false, status: 'error', message: abn.length > 0 ? 'ABN must be 11 digits (or 9 digits for ACN).' : '' };
            this.abnRecordFound = false;
            this.abnDetails = null;
            return;
         }
         this.abnLookup.isLoading = true;
         this.abnLookup.message = '';
         this.abnLookup.status = 'idle';
         try {
            const resp = await this.$axios.post('/abn/look-up', {
               abn: abn,
            });
            const details = resp.data.abn_details;
            this.abnDetails = details;
            this.abnRecordFound = true;

            if (this.lead.isCharity && !details.is_charity) {
               this.abnLookup.status = 'error';
               this.abnLookup.message = 'ABN is found but not registered as a charity.';
            } else {
               this.abnLookup.status = 'success';
               this.abnLookup.message = 'ABN Validated successfully.';
               this.lead.organisation = details.entity_name;
            }
         } catch (error) {
            this.abnRecordFound = false;
            this.abnDetails = null;
            this.abnLookup.status = 'error';
            this.abnLookup.message = error.response?.data?.message || 'Invalid ABN';
         } finally {
            this.abnLookup.isLoading = false;
         }
      },

      async validateForm() {
         const isFormCorrect = await this.v$.$validate()

         if (this.lead.isCharity && (!this.abnRecordFound || this.abnLookup.status !== 'success')) {
            this.$toast.error("A valid and successfully verified charity ABN is required.");
            this.isFormValid = false;
            return;
         }

         if (!isFormCorrect) {
            this.isFormValid = false

            this.$nextTick(() => {

               //check if the first occurrence of error is from the test type form
               let accordionFormErr = document.querySelector(".error-msg").closest(".test-type-form")
               if (accordionFormErr != null) {

                  //collapse all accordion first
                  const collapseElementList = document.querySelectorAll('.test-type-form')

                  collapseElementList.forEach((el) => {

                     if (el != accordionFormErr) {
                        el.querySelector(".accordion-button").classList.add('collapsed')
                        el.querySelector(".accordion-collapse").classList.remove('show')
                     }

                  })

                  document.querySelector(".test-type-error-msg").classList.add('text-danger')

                  accordionFormErr.querySelector(".accordion-button").classList.remove('collapsed')
                  accordionFormErr.querySelector(".accordion-collapse").classList.add('show')
               } else {
                  if (document.querySelector(".test-type-error-msg") != null) {
                     document.querySelector(".test-type-error-msg").classList.remove('text-danger')
                  }
               }

               let domRect = document.querySelector('.error-msg').parentNode.getBoundingClientRect();

               window.scrollTo(
                  domRect.left + document.documentElement.scrollLeft,
                  domRect.top + document.documentElement.scrollTop - 5
               );
            })
         } else {
            this.isFormValid = true
            this.createLead()
         }
      },

      async createLead() {
         this.isProcessing = true;

         if (this.lead.organisation) {
            this.lead.organisation = this.lead.organisation.replace(/[<>"\\`]/g, '').trim();
         }
         if (this.lead.contactNumber.trim().length < 5) {
            this.lead.contactNumber = '';
         }

         let lead = {
            // ... your lead object creation
            organisation_name: this.lead.organisation,
            first_name: this.lead.firstName,
            last_name: this.lead.lastName,
            email: this.lead.email,
            phone_number: this.lead.contactNumber,
            abn: this.lead.abn,
            is_charity: this.lead.isCharity,
            marketing_channel_id: this.chosen_marketingChannel,
            lead_notes: [{
               note_type: "initial_note_pentest",
               note_details: "Requested Penetration Testing for: " + this.testTypes.map(e => (e.name)).toString().replaceAll(",", ", ")
            }],
            country_code: this.lead.selectedCountry.code
         };

         // get each product id
         lead.products = [];
         this.lead.product.forEach((p) => {
            let pentestProduct = p.id.toString();
            lead.products.push({
               product_id: pentestProduct
            });
         });

         //create object for each test type
         lead.penetration_test = [];
         this.testTypes.forEach((t) => {
            lead.penetration_test.push(this.pentestDetails(t.name));
         });

         try {
            const response = await this.$axios_internal.post("pentest/leads", lead);

            if (response.data.success) {
               // Form submitted successfully, navigate to the thank you page
               //this.$toast.success(response.data.message);
               this.resetForm();
               // Redirect to the thank you page route
              
               if (this.isScheduleMeeting) {
                  this.openCalendar()
               } else {
                   this.openThankYouPageInTab()
               }

            } else {
               this.$toast.error(response.data.message);
            }
         } catch (error) {
            this.$toast.error(error.response?.data?.message || 'An error occurred.');
         } finally {
            this.isProcessing = false;
         }
      },

      openThankYouPageInTab() {
         this.$router.push({ name: 'ThankYouPagePentester' });
      },


      pentestDetails(testType) {
         let productId = this.testTypes.filter(e => { return e.name == testType }).map(e => (e.id)).toString()

         let pentest = {
            has_timeframe: this.penetrationTest.hasTimeframe,
            timeframe_details: this.penetrationTest.timeframeDetails,
            relevant_information: this.penetrationTest.relevantInformation,
            test_type: productId
         }

         if (testType == APP) {
            pentest.app = this.$refs.appPenTestFormRef[0].getAppPenTestDetails()
         } else if (testType == INFRA) {
            pentest.network_infra = this.$refs.networkInfraPenTestFormRef[0].getNetworkInfraPenTestDetails()
         } else if (testType == WEB) {
            pentest.website = this.$refs.webPenTestFormRef[0].getWebPenTestDetails()
         }

         return pentest

      },

      openCalendar() {
         const calendarUrl = process.env.VUE_APP_GOOGLE_CALENDAR_LINK;

         // Check if the URL exists to avoid redirecting to a blank page
         if (calendarUrl) {
            // This line redirects the current tab to the new URL
            window.location.href = calendarUrl;
         } else {
            console.error('Calendar link is not defined.');
            // Optionally, show an error message to the user
         }
      },

      async getProductList() {
         await this.$axios_internal.get("pentest/products")
            .then((response) => {
               this.productList = response.data.products
            })
            .catch((error) => {
               this.$toast.error(error.response.data.message)
            })
      },

      async getMarketingChannelList() {

         await this.$axios.get("/website/categories?name=MarketingChannel")
            .then((response) => {
               this.marketingChannels = response.data.category.subcategories
            })
            .catch((error) => {
               this.$toast.error(error.response.data.message)
            })
      },

      checkSelectedProduct() {

         if (this.lead.product.length != 0) {
            this.testTypes = []
            this.lead.product.forEach((product) => {

               let pentestProduct = this.productList.filter(e => { return e.id == product.id }).map(e => (e.name)).toString()

               pentestProduct = pentestProduct.split("PenetrationTest")

               pentestProduct = pentestProduct[1].replace("_", "")

               this.testTypes.push({
                  id: product.id,
                  name: pentestProduct
               })

            });

         } else {
            this.testTypes = []
         }

      },

      productName(productName) {
         let name = productName.split("PenetrationTest")[1]

         if (name == APP) {
            return "Mobile App"
         } else if (name == INFRA) {
            return "Network / Infrastructure"
         } else if (name == WEB) {
            return "Website"
         }

         return ""
      },

      testType(productName, testType) {
         let name = productName.split("PenetrationTest")[1]

         return name == testType

      },

      hasTestType(type) {
         return this.testTypes.map(e => (e.name)).includes(type)
      },

      // reset some inputs that are dependent on radio button value
      onChange() {
         if (!this.penetrationTest.hasTimeframe) {
            this.penetrationTest.timeframeDetails = ''
            this.v$.penetrationTest.timeframeDetails.$reset()
         }
      },

   },
};

</script>

<style>
.requiredField {
   color: red;
}

.loading {
   position: fixed;
   top: 0;
   left: 0;
   right: 0;
   bottom: 0;
   z-index: 999;
   background-color: rgb(0, 0, 0, 0.2);
   height: 100vh;
   display: flex;
   flex-direction: column;
   justify-content: center;
   align-items: center;
}

/* Fix for the blue hover and "Press enter to select" background */
.multiselect__option--highlight {
   background: #00a0c1 !important;
   color: #fff !important;
}

/* Target the pseudo-element with the blue background */
.multiselect__option--highlight::after {
   background: #00a0c1 !important;
   color: #fff !important;
}
</style>